import React, { useEffect, useMemo, useState } from 'react'

import { getErrorMessage } from 'utils'

import type { CertificateAdmin } from 'types'

import { useSnackbar } from 'contexts'
import { useCertificateDeleteMutations, useCertificateGroup, useCertificateGroupDelete } from 'hooks/api'

import { Box, Checkbox, Stack, Typography } from '@mui/material'

import { Modal, Button, Alert } from '@repo/ui'
import { CertificatePreview, CertificateGroupMemberPreview, Search } from 'components'

interface DeleteCertificateModalProps {
  certificate: CertificateAdmin | null
  open: boolean
  onClose: () => void
  refetch: () => void
}

// VRG-231 review round 2 item 9 — a certificate that is a group PARENT deletes
// differently depending on divisibility: an INDIVISIBLE group's children are
// deleted along with it (they can't exist standalone); a DIVISIBLE group's
// children are split out into their own standalone certificates instead. In
// both cases the user can additionally choose specific children to delete
// alongside the parent, via the same search + checkbox-list idiom as
// AssignUsersModal (§28) rendering each child with the shared
// CertificateGroupMemberPreview (§36).
export const DeleteCertificateModal = (props: DeleteCertificateModalProps) => {
  const { certificate, open, onClose = () => {}, refetch } = props

  const { open: notification } = useSnackbar()

  const isGroupParent = Boolean(certificate?.group_divisibility) && !certificate?.parent_certificate_id

  const { data: groupData, isLoading: isGroupLoading } = useCertificateGroup(
    { certificateId: certificate?.id as string },
    { enabled: open && isGroupParent },
  )
  const children = groupData?.children ?? []

  const [search, setSearch] = useState('')
  const [selectedChildIds, setSelectedChildIds] = useState<Set<string>>(new Set())

  // Reset selection whenever the modal opens/closes or targets a different certificate.
  useEffect(() => {
    if (!open) {
      setSearch('')
      setSelectedChildIds(new Set())
    }
  }, [open, certificate?.id])

  const filteredChildren = useMemo(() => {
    if (!search.trim()) return children
    const q = search.toLowerCase()
    return children.filter(
      child => (child.product?.title ?? '').toLowerCase().includes(q) || child.id.toLowerCase().includes(q),
    )
  }, [children, search])

  const toggleChild = (id: string) => {
    setSelectedChildIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleSelectAll = () => setSelectedChildIds(new Set(children.map(child => child.id)))
  const handleDeselectAll = () => setSelectedChildIds(new Set())

  const [{ remove }, { isLoading: isDeletingSingle }] = useCertificateDeleteMutations({
    onSuccess: () => {
      handleClose()
      refetch()
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      notification({ type: 'error', title: message })
    },
  })

  // VRG-231 review round 2 item 9 — a group PARENT deletes through the
  // dedicated group-delete endpoint (accepts `childIdsToDelete`), never the
  // plain single-certificate one above (which has no concept of a group and
  // would 400 against a parent).
  const { deleteCertificateGroup, isDeletingGroup } = useCertificateGroupDelete({
    onSuccess: (data) => {
      notification({
        type: 'success',
        title:
          data.splitIds.length > 0
            ? `Certificate group deleted. ${data.splitIds.length} child${data.splitIds.length > 1 ? 'ren' : ''} split out as standalone.`
            : 'Certificate group deleted',
      })
      handleClose()
      refetch()
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      notification({ type: 'error', title: message })
    },
  })

  const isLoading = isDeletingSingle || isDeletingGroup

  const handleDelete = () => {
    if (!certificate) return
    if (certificate.status !== 'inactive') {
      return notification({
        type: 'error',
        title: 'Only inactive certificates can be deleted',
      })
    }
    if (isGroupParent) {
      deleteCertificateGroup({ certificateId: certificate.id, childIdsToDelete: Array.from(selectedChildIds) })
      return
    }
    remove({ id: certificate.id })
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      title="Delete certificate"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            destructive
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Are you sure you want to delete the following certificate?
        </Typography>
        <CertificatePreview certificate={certificate as any} size="medium" />

        {isGroupParent && (
          <>
            <Alert
              severity="warning"
              description={
                certificate?.group_divisibility === 'indivisible'
                  ? 'This is an indivisible group. Deleting it will also delete all of its children. This cannot be undone.'
                  : 'This is a divisible group. Deleting it will split its children out into their own standalone certificates, unless you choose to delete them below.'
              }
            />

            {!isGroupLoading && children.length > 0 && (
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body3" sx={{ fontWeight: 500 }}>
                    Choose children to also delete
                  </Typography>
                  <Stack direction="row" spacing={3}>
                    <Button variant="text" size="small" onClick={handleSelectAll} disabled={isLoading}>
                      Select all
                    </Button>
                    <Button variant="text" size="small" onClick={handleDeselectAll} disabled={isLoading}>
                      Deselect all
                    </Button>
                  </Stack>
                </Stack>

                <Search
                  value={search}
                  onChange={setSearch}
                  onClear={() => setSearch('')}
                  placeholder="Search children"
                  fullWidth
                  debounce={false}
                />

                <Box
                  sx={theme => ({
                    maxHeight: 280,
                    overflowY: 'auto',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                  })}
                >
                  {filteredChildren.length === 0 ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                      <Typography variant="body3" color="text.secondary">
                        No children match your search.
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack spacing={2} sx={{ p: 2 }}>
                      {filteredChildren.map(child => (
                        // No whole-row onClick here (unlike AssignUsersModal) — the
                        // shared CertificateGroupMemberPreview's thumbnail/title is
                        // itself a Link to that certificate's own page (so a reviewer
                        // can inspect a child before deciding to also delete it); only
                        // the checkbox toggles the selection, so the two affordances
                        // don't fight over the same click.
                        <Stack key={child.id} direction="row" alignItems="center" spacing={2}>
                          <Checkbox
                            checked={selectedChildIds.has(child.id)}
                            onChange={() => toggleChild(child.id)}
                            size="small"
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <CertificateGroupMemberPreview certificate={child} size={40} />
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Modal>
  )
}
